/**
 * Autoplay Manager Hook
 * Manages video autoplay based on which section is visible
 * - Hero section: 5 second delay on initial load
 * - Other sections: 2 second delay when scrolling into view
 * - Pauses when leaving a section
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { useIntersectionObserver } from './useIntersectionObserver';

// Section IDs mapped to their display names
const SECTION_IDS = {
  'study-anchor': 'hero',
  'gym-anchor': 'gym',
  'office-anchor': 'office',
  'closet-anchor': 'closet',
};

export const useAutoplayManager = (options = {}) => {
  const {
    heroDelay = 5000,    // 5 seconds for hero
    sectionDelay = 2000, // 2 seconds for other sections
  } = options;

  // Track which section is currently visible
  const activeSection = useIntersectionObserver();

  // Track which section should have autoplay enabled
  const [autoplaySection, setAutoplaySection] = useState(null);

  // Track if this is the initial page load
  const isInitialLoad = useRef(true);

  // Timer ref to clear on section change
  const timerRef = useRef(null);

  // Clear any pending timer
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    // Clear previous timer when section changes
    clearTimer();

    if (!activeSection) {
      setAutoplaySection(null);
      return;
    }

    // Determine the delay based on section and load state
    const isHero = activeSection === 'study-anchor';
    const delay = isHero && isInitialLoad.current ? heroDelay : sectionDelay;

    // After initial load, mark it as complete
    if (isInitialLoad.current && activeSection) {
      isInitialLoad.current = false;
    }

    // Set a timer to enable autoplay after the delay
    timerRef.current = setTimeout(() => {
      setAutoplaySection(activeSection);
    }, delay);

    // Cleanup on unmount or section change
    return () => {
      clearTimer();
    };
  }, [activeSection, heroDelay, sectionDelay, clearTimer]);

  // Check if a specific section should autoplay
  const shouldAutoplay = useCallback((sectionId) => {
    return autoplaySection === sectionId;
  }, [autoplaySection]);

  return {
    activeSection,
    autoplaySection,
    shouldAutoplay,
  };
};

export default useAutoplayManager;
